import React, { useState } from 'react'
import InstructorSidebar from '../common/instructorSidebar'
import { Link } from 'react-router-dom'
import ImageWithBasePath from '../../../core/common/imageWithBasePath'
import { all_routes } from '../../router/all_routes'
import Table from '../../../core/common/dataTable/index'
import AddCategoriesModal from '../../../core/common/modal/AddCategoresModal'
import { useNotify } from '../../../provider/NotificationProvider'
import { useLiveTeachingList } from './hooks/useLiveTeaching'
import { rupiahFormatter } from '../../../utils/helpers'
import ConfirmDeleteModal from '../../../core/common/modal/ConfirmDeleteModal'
import CopyLinkDropdown from '../../../core/common/modal/CopyLinkDropdown'
import { useMutation } from '@tanstack/react-query'
import {
  deleteCourse,
  postPublishCourse,
  postUnpublishCourse,
  putCourseUpdate,
} from '../../../api/courses'
import { Switch, DatePicker, Button } from 'antd'
import dayjs from 'dayjs'

const LiveTeachingList = () => {
  const notify = useNotify()
  const {
    data,
    isLoading,
    page,
    limit,
    search,
    setSearch,
    onPaginationChange,
    refetch,
  } = useLiveTeachingList({})

  const [showModal, setShowModal] = useState(false)
  const [selectedId, setSelectedId] = useState<number | null>(null)

  const liveTeaching = data?.data ?? []
  // const summary = data?.summary ?? null
  const total = data?.total ?? 0

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteCourse(id, true),
    onSuccess: () => {
      notify.success({
        message: 'Delete berhasil',
        description: 'Data live teaching berhasil dihapus',
      })
      refetch()
      handleCloseModal()
    },
    onError: (err) => {
      notify.error({
        message: 'Delete live teaching gagal',
        description: err?.message || 'Terjadi kesalahan.',
      })
      handleCloseModal()
    },
  })

  const publishMutation = useMutation({
    mutationFn: (id: number) => postPublishCourse(id, true),
    onSuccess: () => {
      notify.success({
        message: 'Publish berhasil',
        description: 'Data live teaching berhasil dipublish',
      })
      refetch()
    },
    onError: (err) => {
      notify.error({
        message: 'Publish live teaching gagal',
        description: err?.message || 'Terjadi kesalahan.',
      })
    },
  })

  const [scheduleDates, setScheduleDates] = useState<Record<number, any>>({})

  const scheduleMutation = useMutation({
    mutationFn: ({ id, date }: { id: number; date: any }) => {
      const fd = new FormData()
      if (date) {
        fd.append('publish_at', date.format('YYYY-MM-DD HH:mm:ss'))
        fd.append('auto_publish', '1')
      } else {
        fd.append('publish_at', '')
        fd.append('auto_publish', '0')
      }
      return putCourseUpdate(String(id), fd, true)
    },
    onSuccess: () => {
      notify.success({
        message: 'Schedule berhasil',
        description: 'Jadwal publish berhasil disimpan',
      })
      refetch()
    },
    onError: (err: any) => {
      notify.error({
        message: 'Schedule gagal',
        description: err?.message || 'Terjadi kesalahan.',
      })
    },
  })

  const unpublishMutation = useMutation({
    mutationFn: (id: number) => postUnpublishCourse(id, true),
    onSuccess: () => {
      notify.success({
        message: 'Unpublish berhasil',
        description: 'Data live teaching berhasil diunpublish',
      })
      refetch()
    },
    onError: (err) => {
      notify.error({
        message: 'Unpublish live teaching gagal',
        description: err?.message || 'Terjadi kesalahan.',
      })
    },
  })

  const columns = [
    {
      title: 'Course Name',
      dataIndex: 'title',
      render: (text: string, record: any) => (
        <div className="d-flex align-items-center">
          <div
            // to={`${all_routes.adminLiveTeaching}/detail/${record.id}`}
            className="avatar avatar-lg me-2 flex-shrink-0"
          >
            <ImageWithBasePath
              className="img-fluid object-fit-cover"
              srcApi={record?.image}
              src=""
              alt=""
            />
          </div>
          <div style={{ minWidth: 0, maxWidth: '300px' }}>
            <h6 className="fw-medium mb-2 text-truncate" title={text}>
              {/* <Link to={`${all_routes.adminLiveTeaching}/detail/${record.id}`}>
              </Link> */}
              {text}
            </h6>
            <div className="d-flex align-items-center">
              <span className="d-inline-flex fs-12 align-items-center me-2 pe-2 border-end">
                <i className="isax isax-video-circle me-1 text-gray-9 fw-bold" />
                {record?.studied?.length || record?.topics_count || 0} Topics
              </span>
              <span className="d-inline-flex fs-12 align-items-center me-2 pe-2 border-end">
                <i className="isax isax-message-question me-1 text-gray-9 fw-bold" />
                {record?.course_level || '-'}
              </span>
              {/* <span className="d-inline-flex fs-12 align-items-center">
                <i className="isax isax-clock me-1 text-gray-9 fw-bold" />
                03:15:00 Hours
              </span> */}
            </div>
          </div>
        </div>
      ),
      sorter: (a: any, b: any) => a.title.length - b.title.length,
    },

    {
      title: 'Students',
      dataIndex: 'max_student',
      sorter: (a: any, b: any) => a.max_student.length - b.max_student.length,
    },
    {
      title: 'Price',
      dataIndex: 'price',
      sorter: (a: any, b: any) => a.price.length - b.price.length,
      render: (text: string) => rupiahFormatter(text),
    },
    {
      title: 'JPL',
      dataIndex: 'certificate_jpl_total',
      render: (text: string) => text || '-',
      sorter: (a: any, b: any) =>
        String(a?.certificate_jpl_total || '').length -
        String(b?.certificate_jpl_total || '').length,
    },
    // {
    //   title: "Ratings",
    //   dataIndex: "Ratings",
    //   render: (text: string) => (
    //     <div className="d-flex align-items-center">
    //       <i className="fa-solid fa-star fs-12 filled text-warning me-1" />
    //       <span>{text}</span>
    //     </div>
    //   ),
    //   sorter: (a: any, b: any) => a.Ratings.length - b.Ratings.length,
    // },
    {
      title: 'Published',
      dataIndex: 'published',
      render: (text: string, record: any) => (
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          <Switch
            loading={publishMutation.isPending || unpublishMutation.isPending}
            checked={record?.status === 'PUBLISHED'}
            onChange={() => {
              if (record?.status === 'PUBLISHED') {
                unpublishMutation.mutate(record?.id)
              } else {
                publishMutation.mutate(record?.id)
              }
            }}
          />
          <DatePicker
            showTime
            format={'DD-MM-YYYY hh:mm A'}
            placeholder="Schedule publish"
            style={{ minWidth: 180 }}
            value={
              scheduleDates[record.id]
                ? dayjs(scheduleDates[record.id])
                : record?.publish_at
                ? dayjs(record.publish_at)
                : null
            }
            onChange={(date) => {
              setScheduleDates((prev) => ({
                ...prev,
                [record.id]: date ? date.toDate() : null,
              }))
              if (!date) scheduleMutation.mutate({ id: record.id, date: null })
            }}
          />
          <Button
            size="small"
            style={{ whiteSpace: 'nowrap' }}
            loading={scheduleMutation.isPending}
            onClick={() => {
              const selected = scheduleDates[record.id]
                ? dayjs(scheduleDates[record.id])
                : record?.publish_at
                ? dayjs(record.publish_at)
                : null
              if (selected) scheduleMutation.mutate({ id: record.id, date: selected })
            }}
          >
            Set
          </Button>
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (text: string) => (
        <span
          className={`badge badge-sm ${
            text === 'UNPUBLISHED'
              ? 'bg-skyblue'
              : text === 'DRAFT'
              ? 'bg-info'
              : 'bg-success'
          } d-inline-flex align-items-center me-1`}
        >
          <i className="fa-solid fa-circle fs-5 me-1" />
          {text}
        </span>
      ),
      sorter: (a: any, b: any) => a.status.length - b.status.length,
    },
    {
      title: 'Action',
      dataIndex: '',
      render: (_: string, record: any) => (
        <div className="d-flex align-items-center">
          <Link
            to={`${all_routes.adminLiveTeaching}/${record.id}`}
            className="d-inline-flex fs-14 me-1 action-icon"
          >
            <i className="isax isax-edit-2" />
          </Link>
          <CopyLinkDropdown
            record={{
              id: record.id,
              type: 'live_teaching',
            }}
          />
          <Link
            to="#"
            className="d-inline-flex fs-14 action-icon"
            onClick={() => openDeleteModal(record.id)}
          >
            <i className="isax isax-trash" />
          </Link>
          <Link
            to={`${all_routes.adminCourses}/analytics/${record.id}`}
            className="d-inline-flex fs-14 action-icon"
            title="View Analytics"
          >
            <i className="isax isax-chart-214" />
          </Link>
        </div>
      ),
    },
  ]

  const openDeleteModal = (id: number) => {
    setSelectedId(id)
    setShowModal(true)
  }

  const handleCloseModal = () => {
    setShowModal(false)
    setSelectedId(null)
  }

  const handleConfirmDelete = () => {
    if (selectedId !== null) {
      deleteMutation.mutate(selectedId)
    }
  }
  return (
    <>
      <div className="content">
        <div className="container">
          <div className="row">
            <InstructorSidebar />

            <div className="col-lg-9">
              <div className="page-title d-flex align-items-center justify-content-between">
                <h5 className="fw-bold">Live Teaching</h5>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <Link
                    to="#"
                    className="btn btn-secondary rounded-pill"
                    data-bs-toggle="modal"
                    data-bs-target="#add_categories"
                  >
                    <i className="isax isax-eye me-2 fs-10" />
                    Categories
                  </Link>
                  <Link
                    to={all_routes.adminAddLiveTeaching}
                    className="btn btn-primary rounded-pill"
                  >
                    <i className="isax isax-add-circle me-2 fs-10" />
                    Add Content
                  </Link>
                </div>
              </div>
              <div className="row justify-content-end">
                <div className="col-md-4">
                  <div className="input-icon mb-3">
                    <span className="input-icon-addon">
                      <i className="isax isax-search-normal-14" />
                    </span>
                    <input
                      type="search"
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                      placeholder="Search..."
                      className="form-control form-control-md"
                    />
                  </div>
                </div>
              </div>
              <Table
                columns={columns}
                dataSource={liveTeaching}
                isLoading={isLoading}
                pagination={{
                  current: page,
                  pageSize: limit,
                  total: total,
                  last_page: data?.last_page,
                }}
                onChange={onPaginationChange}
              />
            </div>
          </div>
        </div>
      </div>
      <>
        <ConfirmDeleteModal
          show={showModal}
          onClose={handleCloseModal}
          onConfirm={handleConfirmDelete}
          title="Delete Live Teaching"
          message="Are you sure you want to delete this data?"
        />
        <AddCategoriesModal type="live_teaching" />
      </>
    </>
  )
}

export default LiveTeachingList
